import React, { useMemo, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Polyline,
  CircleMarker,
  Popup,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";

import { getCoords } from "./geoHelper";
import { normalizeAddress } from "./addressNormalizer";

const STORE_COORDS = {
  lat: 45.4913,
  lng: -73.8197,
};
export const getOrderAddresses = (order) => {
    const normalized = normalizeAddress(order);
    return {
      fullAddress: normalized.full,
      gpsAddress: normalized.forGPS,
    };
  };
// DeliveryManifest.js
const DeliveryManifest = ({ 
  deliveries,        // This catches 'optimizedDeliveries'
  setDeliveries,     // This catches 'setOptimizedDeliveries'
  optimizedStops, 
  setOptimizedStops, 
  optimizedRoute, 
  setOptimizedRoute 
}) => {
  // We KEEP these two because they are only for THIS component's UI
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizeError, setOptimizeError] = useState("");

  
  // 1. First, make sure you have this (you already do)
const safeDeliveries = useMemo(() => deliveries || [], [deliveries]);

// 2. ADD THIS LINE - This defines the missing variable
const deliveriesToDisplay = optimizedStops && optimizedStops.length > 0 ? optimizedStops : safeDeliveries;

  if (!safeDeliveries || safeDeliveries.length === 0) {
    return null;
  }


  const openInMaps = (order) => {
    const { fullAddress } = getOrderAddresses(order);
    const encodedAddress = encodeURIComponent(fullAddress);
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`,
      "_blank"
    );
  };

  const openFullRouteInMaps = () => {
    if (safeDeliveries.length === 0) return;

    const origin = encodeURIComponent(`${STORE_COORDS.lat},${STORE_COORDS.lng}`);
    const addresses = safeDeliveries.map(
      (order) => getOrderAddresses(order).fullAddress
    );

    const destination = encodeURIComponent(addresses[addresses.length - 1] || "");
    const waypoints = addresses
      .slice(0, -1)
      .map((addr) => encodeURIComponent(addr))
      .join("|");

    const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&waypoints=${waypoints}&travelmode=driving`;
    window.open(url, "_blank");
  };

  const optimizeRoute = async () => {
    try {
      setIsOptimizing(true);
      setOptimizeError("");
      setOptimizedRoute([]);
      setOptimizedStops([]);

      const jobsResults = await Promise.all(
        safeDeliveries.map(async (order, index) => {
          const { fullAddress, gpsAddress } = getOrderAddresses(order);
          const coords = await getCoords(gpsAddress);

          if (!coords) return null;

          return {
            id: order._id || `job_${index}`,
            location: [coords.lng, coords.lat],
            originalOrder: order,
            fullAddress,
            gpsAddress,
            lat: coords.lat,
            lng: coords.lng
          };
        })
      );

      const validJobs = jobsResults.filter(Boolean);

      if (validJobs.length === 0) {
        setOptimizeError("No valid delivery coordinates were found.");
        return;
      }

      const response = await fetch(
        "https://geoapify-paradise.onrender.com/optimize",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            drivers: [
              {
                id: "driver_1",
                start_location: [STORE_COORDS.lng, STORE_COORDS.lat],
              },
            ],
            jobs: validJobs.map((job) => ({
              id: job.id,
              location: job.location,
            })),
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Backend HTTP ${response.status}`);
      }

      const data = await response.json();
      console.log("Optimize API response:", data);

      // Support for both Geoapify (features) and VROOM (routes) response structures
      const routeCoords =
        data?.features?.[0]?.geometry?.coordinates?.map(([lng, lat]) => [lat, lng]) ||
        data?.routes?.[0]?.geometry?.coordinates?.map(([lng, lat]) => [lat, lng]) || [];

      setOptimizedRoute(routeCoords);

      const steps = data?.features?.[0]?.properties?.actions || 
                    data?.routes?.[0]?.steps || 
                    data?.routes?.[0]?.visits || [];

      if (steps.length > 0) {
        const orderedStops = steps
          .filter(step => step.type === "delivery" || step.job_id)
          .map((step) => {
            const stepId = step.job_id || step.id;
            const matchedJob = validJobs.find((job) => job.id === stepId);

            return matchedJob
              ? {
                  ...matchedJob.originalOrder,
                  routePosition: step.sequence || step.order || null,
                  routeCoords: [matchedJob.lng, matchedJob.lat],
                  lat: matchedJob.lat,
                  lng: matchedJob.lng
                }
              : null;
          })
          .filter(Boolean);

        if (orderedStops.length > 0) {
          setOptimizedStops(orderedStops);
        }
      }
    } catch (error) {
      console.error("Optimization error:", error);
      setOptimizeError("Failed to optimize route.");
    } finally {
      setIsOptimizing(false);
    }
  };

 const updateDriverAssignment = (id, driverValue) => {
  if (typeof setDeliveries !== 'function') {
    console.error("Critical: setDeliveries is missing!");
    return;
  }

  const updated = deliveries.map(order => 
    (order._id === id || order.index === id) 
      ? { ...order, assignedDriver: driverValue } 
      : order
  );

  setDeliveries(updated); // This will now work without crashing!
};

  // This sends the updated list back up to the AdminPanel


 return (
    <div className="mt-8 bg-white shadow-2xl rounded-2xl overflow-hidden border border-gray-100 manifest-container">
      <div className="bg-gradient-to-r from-blue-700 to-indigo-600 p-5 flex justify-between items-center text-white no-print">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-3">
            📋 Driver's Optimized Manifest
          </h2>
          <p className="text-blue-100 mt-1">
            {safeDeliveries.length} stops • Route starts from Store (DDO)
          </p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead className="bg-gray-50">
            <tr className="text-xs uppercase font-semibold text-gray-500 border-b">
              <th className="px-6 py-4 w-12">#</th>
              <th className="px-6 py-4">Customer</th>
              <th className="px-6 py-4">Assign Driver</th>
              <th className="px-6 py-4">Address</th>
              <th className="px-6 py-4 text-center no-print">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {deliveriesToDisplay.map((order, index) => {
              const { fullAddress } = getOrderAddresses(order);

              return (
                <tr
                  key={order._id || index}
                  className="hover:bg-blue-50 transition-colors border-b"
                >
                  <td className="px-6 py-5 font-bold text-blue-600 text-lg">
                    {index + 1}
                  </td>

                  <td className="px-6 py-5">
                    <div className="font-bold text-gray-900">{order.name}</div>
                    <div className="text-sm text-gray-500">{order.phone}</div>
                  </td>

                  <td className="px-6 py-5">
                    <select 
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
                      value={order.assignedDriver || ""} 
                      onChange={(e) => updateDriverAssignment(order._id || index, e.target.value)}
                    >
                      <option value="">Unassigned</option>
                      <option value="1">Driver 1 (Local)</option>
                      <option value="2">Driver 2 (City)</option>
                    </select>
                  </td>

                  <td className="px-6 py-5 text-gray-700">
                    <div className="font-medium">{fullAddress}</div>
                    {order.specialNotes && (
                      <div className="mt-1 text-xs text-red-600 font-bold italic">
                        ⚠️ {order.specialNotes}
                      </div>
                    )}
                  </td>

                  <td className="px-6 py-5 text-center no-print">
                    <button
                      onClick={() => openInMaps(order)}
                      className="inline-flex items-center gap-2 bg-white border border-gray-300 hover:border-blue-500 hover:text-blue-600 px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-sm"
                    >
                      📍 Navigate
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {optimizeError && (
        <div className="px-5 py-3 bg-red-50 text-red-700 border-t border-red-200 no-print">
          {optimizeError}
        </div>
      )}

      {optimizedRoute.length > 0 && (
        <div className="p-5 border-t bg-white no-print">
          <h3 className="text-lg font-bold text-gray-800 mb-3">
            🗺️ Optimized Route Preview
          </h3>

          <div className="h-[420px] w-full rounded-xl overflow-hidden border">
            <MapContainer
              center={[STORE_COORDS.lat, STORE_COORDS.lng]}
              zoom={11}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

              <CircleMarker center={[STORE_COORDS.lat, STORE_COORDS.lng]} radius={8} color="red">
                <Popup>Store Start Point</Popup>
              </CircleMarker>

              {deliveriesToDisplay.map((order, index) => {
                let lat = order.lat || null;
                let lng = order.lng || null;
                if (lat === null || lng === null) return null;

                const jitter = 0.00005; 
                const latJitter = (Math.random() - 0.5) * jitter;
                const lngJitter = (Math.random() - 0.5) * jitter;

                return (
                  <CircleMarker 
                    key={order._id || index} 
                    center={[lat + latJitter, lng + lngJitter]} 
                    radius={6}
                  >
                    <Popup>
                      <div className="text-sm">
                        <strong className="block border-b mb-1">
                          Stop {index + 1}: {order.name}
                        </strong>
                        <span className="text-gray-600">
                          {getOrderAddresses(order).fullAddress}
                        </span>
                        {order.unit && <div className="mt-1 font-bold">Unit: {order.unit}</div>}
                      </div>
                    </Popup>
                  </CircleMarker>
                );
              })}

              <Polyline positions={optimizedRoute} color="blue" weight={3} opacity={0.7} />
            </MapContainer>
          </div>
        </div>
      )}

      <div className="p-4 bg-light border-top d-flex gap-3 justify-content-end no-print flex-wrap">
        <button
          onClick={optimizeRoute}
          disabled={isOptimizing}
          className="btn btn-primary btn-lg fw-bold px-4 shadow-sm"
          style={{ minWidth: '200px', backgroundColor: '#007bff', borderColor: '#0056b3' }}
        >
          {isOptimizing ? "⏳ Optimizing..." : "⚡ Optimize Route"}
        </button>

  {/* GOOGLE MAPS BUTTON - Solid Green */}
  <button
    onClick={openFullRouteInMaps}
    className="btn btn-success btn-lg fw-bold px-4 shadow-sm"
    style={{ backgroundColor: '#28a745', borderColor: '#1e7e34' }}
  >
    🗺️ Open Google Maps
  </button>

  {/* PRINT BUTTON - Dark Gray/Black */}
  <button
    onClick={() => window.print()}
    className="btn btn-dark btn-lg fw-bold px-4 shadow-sm"
    style={{ backgroundColor: '#343a40', borderColor: '#23272b' }}
  >
    🖨️ Print Manifest
  </button>
</div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
            @media print {
              body * {
                visibility: hidden;
              }

              .manifest-container,
              .manifest-container * {
                visibility: visible;
              }

              .manifest-container {
                position: absolute;
                left: 0;
                top: 0;
                width: 100%;
                box-shadow: none !important;
                border: none !important;
                margin: 0 !important;
                padding: 0 !important;
              }

              .no-print {
                display: none !important;
              }

              table {
                border-collapse: collapse;
                width: 100%;
              }

              th, td {
                border: 1px solid #dee2e6 !important;
                padding: 8px !important;
              }

              .bg-gradient-to-r,
              .bg-blue-600 {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
            }
          `,
        }}
      />
    </div>
  );
};

export default DeliveryManifest;